import {
  Box,
  chakra,
  Divider,
  Flex,
  Heading,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalOverlay,
} from '@chakra-ui/react';
import {
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { AdminUserListViewQuery } from '@/@generated/codegen/API';
import { usePaginationState } from '@/hooks/usePaginationState';
import { useTableQueryParamsUtils } from '@/hooks/useTableQueryParamsUtils';
import ColumnVisibility from '@/ui/TanstackTable/Columns/ColumnVisibility';
import ActiveFilters from '@/ui/TanstackTable/Filter/ActiveFilters/ActiveFilters';
import Filters, { FilterExtraInfo } from '@/ui/TanstackTable/Filter/Filters';
import GlobalFilter from '@/ui/TanstackTable/Filter/GlobalFilter/GlobalFilter';
import { FilterType } from '@/ui/TanstackTable/Filter/NewFilterComponent';
import Pagination from '@/ui/TanstackTable/Pagination';
import TanstackTableView from '@/ui/TanstackTable/TanstackTableView';
import { fuzzyFilter } from '@/utils/helpers/fuzzyFilter';

import useAdminUserTableStore from './AdminUserListTableStore';
import { AdminUserListItemType, getColumns } from './columns';
import ManageUserClientsView from '../MangeUserClientsView/ManageUserClientsView';
import UserMeetingAccessView from '../UserMeetingAccessView/UserMeetingAccessView';

type Props = {
  data: AdminUserListViewQuery;
  pagination: ReturnType<typeof usePaginationState>;
  tablePrefix: string;
};

const filterExtraInfo: FilterExtraInfo<AdminUserListItemType> = [
  {
    filterId: 'email',
    filterType: FilterType.STRING,
  },
  {
    filterId: 'isGuest',
    filterType: FilterType.BOOLEAN,
  },
  {
    filterId: 'createdAt',
    filterType: FilterType.DATE,
  },
  {
    filterId: 'client',
    filterType: FilterType.STRING,
    queryParamKey: 'clientName',
  },
  {
    filterId: 'team',
    filterType: FilterType.STRING,
    queryParamKey: 'teamName',
  },
];

export const AdminUserListView = ({ data, pagination, tablePrefix }: Props) => {
  const { t } = useTranslation();
  const totalCount = data.listUsers.totalCount;
  const {
    columnFilters,
    sorting,
    globalFilter,
    onColumnFiltersChange,
    onSortingChange,
    onGlobalFilterChange,
  } = useTableQueryParamsUtils(filterExtraInfo, tablePrefix);
  const [columnVisibility, setColumnVisibility, columnSizing, setColumnSizing] =
    useAdminUserTableStore((state) => [
      state.columnVisibility,
      state.setColumnVisibility,
      state.columnSizing,
      state.setColumnSizing,
    ]);
  const [selectedRow, setSelectedRow] = useState<AdminUserListItemType | null>(null);
  const columns = useMemo(() => getColumns(t, setSelectedRow), [t, setSelectedRow]);

  const table = useReactTable({
    data: data.listUsers.edges.map((edge) => edge.node),
    manualPagination: true,
    pageCount: Math.ceil(totalCount / pagination.paginationState.pageSize),
    state: {
      pagination: pagination.paginationState,
      columnSizing,
      columnVisibility,
      sorting,
    },
    onColumnVisibilityChange: (updater) => {
      const newVisibility = typeof updater === 'function' ? updater(columnVisibility) : updater;
      setColumnVisibility(newVisibility);
    },
    onColumnSizingChange: (updater) => {
      const newSizing = typeof updater === 'function' ? updater(columnSizing) : updater;
      setColumnSizing(newSizing);
    },
    onPaginationChange: pagination.createOnPaginationChangeFunc(data.listUsers.pageInfo),
    onGlobalFilterChange: onGlobalFilterChange,
    onSortingChange: onSortingChange,
    filterFns: {
      fuzzy: fuzzyFilter,
    },
    defaultColumn: {},
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    columns: columns,
  });

  return (
    <Box maxW="full" p={{ base: 3, md: 3 }}>
      <Flex justifyContent="space-between" wrap="wrap" gap={2} alignItems="center" my={2}>
        <chakra.h3 fontSize="2xl" fontWeight="bold">
          {t('admin.users.list.title')}
        </chakra.h3>
      </Flex>
      <Flex wrap="wrap" gap={2} alignItems="center" mb="4">
        <ColumnVisibility table={table} />
        <Filters
          columnFilters={columnFilters}
          setColumnFilters={onColumnFiltersChange}
          table={table}
          filterExtraInfo={filterExtraInfo}
        />
        <GlobalFilter
          globalFilter={globalFilter}
          setGlobalFilter={onGlobalFilterChange}
          maxWidth={{ base: 'none', md: '250px' }}
          flex={{ base: 1, md: 'initial' }}
          minW="150px"
        />
        <ActiveFilters
          columnFilters={columnFilters}
          removeItemFunc={(elem) => {
            onColumnFiltersChange(columnFilters.filter((f) => f.id !== elem.id));
          }}
          filterExtraInfo={filterExtraInfo}
        />
        <Pagination
          table={table}
          pageInfo={data.listUsers.pageInfo}
          totalRows={totalCount}
          tablePrefix={tablePrefix}
          ml="auto"
        />
      </Flex>

      <TanstackTableView table={table} />

      <Modal
        isOpen={selectedRow ? true : false}
        onClose={() => {
          setSelectedRow(null);
        }}
      >
        <ModalOverlay />
        <ModalContent>
          <ModalCloseButton />
          <ModalBody>
            <Box p="20px" gap="10px">
              <Heading size="sm" pb="5">
                Manage {selectedRow?.email}
              </Heading>

              {selectedRow && <ManageUserClientsView row={selectedRow} />}
              <Divider marginY="10px" />
              {selectedRow && <UserMeetingAccessView row={selectedRow} />}
            </Box>
          </ModalBody>
        </ModalContent>
      </Modal>
    </Box>
  );
};
